import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { GetVideoResponse, MediaItemSummary, SpendWalletBalance } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { t } from '@fetmode/i18n';
import { formatUsername } from '@fetmode/utils';
import { Button } from '@fetmode/ui';
import { Link, useParams } from 'react-router-dom';

import { useAuth, useAuthModal } from '../auth/index.js';
import { useCoinShop } from '../wallet/index.js';
import {
  GRID_PAGE_SIZE,
  ThumbGridPager,
  usePagedItems,
} from '../../shared/components/ThumbGridPager/index.js';
import '../../shared/styles/thumb-grid.css';
import './VideoDetailPage.css';

function creatorInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const chars = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : trimmed.slice(0, 2);
  return chars.toUpperCase();
}

function RelatedCard({ item }: { item: MediaItemSummary }): ReactElement {
  const priceLabel = item.owned
    ? t('common.videos.owned')
    : item.priceCoins === 0
      ? t('common.videos.free')
      : t('common.videos.coins').replace('{{amount}}', String(item.priceCoins));
  const isFree = !item.owned && item.priceCoins === 0;
  return (
    <article
      className={`thumb-card ${item.owned || isFree ? 'thumb-card--owned' : 'thumb-card--price'}`}
    >
      <Link
        to={`/@${item.model.username}/v/${item.slug}`}
        className="thumb-card__media-link"
        aria-label={item.title}
      >
        <div className={`thumb-card__thumb${item.thumbnailUrl ? '' : ' thumb-card__thumb--empty'}`}>
          {item.thumbnailUrl ? (
            <img src={item.thumbnailUrl} alt="" loading="lazy" width={640} height={360} />
          ) : null}
          <span
            className={`thumb-card__badge ${item.owned || isFree ? 'thumb-card__badge--owned' : 'thumb-card__badge--price'}`}
          >
            {priceLabel}
          </span>
          <div className="thumb-card__meta">
            <h3 className="thumb-card__title">{item.title}</h3>
          </div>
        </div>
      </Link>
    </article>
  );
}

function PagedRelatedGrid({
  items,
  label,
}: {
  items: readonly MediaItemSummary[];
  label: string;
}): ReactElement {
  const paging = usePagedItems(items, GRID_PAGE_SIZE);
  if (items.length === 0) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.videos.relatedEmpty')}</p>;
  }
  return (
    <>
      <ul className="thumb-grid">
        {paging.pageItems.map((relatedItem) => (
          <li key={relatedItem.id}>
            <RelatedCard item={relatedItem} />
          </li>
        ))}
      </ul>
      <ThumbGridPager
        page={paging.page}
        total={paging.total}
        pageSize={GRID_PAGE_SIZE}
        label={label}
        onPageChange={paging.setPage}
      />
    </>
  );
}

export function VideoDetailPage(): ReactElement {
  const { username: rawUsername, slug } = useParams<{ username: string; slug: string }>();
  const username = (rawUsername ?? '').replace(/^@/, '');
  const { api, user } = useAuth();
  const { openAuthModal, requireAuth } = useAuthModal();
  const { openCoinShop, requireCoins, lastWalletTotal } = useCoinShop();
  const [data, setData] = useState<GetVideoResponse | null>(null);
  const [wallet, setWallet] = useState<SpendWalletBalance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!username || !slug) {
      return;
    }

    let cancelled = false;

    void Promise.all([
      api.getVideo(username, slug),
      user ? api.getSpendWallet().catch(() => null) : Promise.resolve(null),
    ])
      .then(([videoResult, walletResult]) => {
        if (!cancelled) {
          setData(videoResult);
          setWallet(walletResult);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('common.videos.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, username, slug, user]);

  useEffect(() => {
    if (lastWalletTotal !== null) {
      setWallet((current) =>
        current ? { ...current, total: lastWalletTotal } : { bonus: 0, gifted: 0, purchased: 0, total: lastWalletTotal },
      );
    }
  }, [lastWalletTotal]);

  const item = data?.item ?? null;

  const handlePurchase = async (): Promise<void> => {
    if (!item) {
      return;
    }
    if (!requireCoins(wallet?.total ?? 0, item.priceCoins, () => {
      void handlePurchase();
    })) {
      return;
    }
    setIsPurchasing(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.purchaseMediaItem(item.id);
      setData((current) => (current ? { ...current, item: result.item } : current));
      setWallet(result.wallet);
      setSuccess(t('common.videos.purchaseSuccess'));
    } catch (err: unknown) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('common.videos.purchaseError');
      setError(message);
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleLikeToggle = (): void => {
    if (!item) {
      return;
    }
    requireAuth(() => {
      setIsLiking(true);
      setError(null);
      const action = item.liked ? api.unlikeMedia(item.id) : api.likeMedia(item.id);
      void action
        .then((result) => {
          setData((current) =>
            current
              ? {
                  ...current,
                  item: { ...current.item, liked: result.liked, likeCount: result.likeCount },
                }
              : current,
          );
        })
        .catch((err: unknown) => {
          setError(err instanceof ApiError ? err.message : t('common.videos.likeError'));
        })
        .finally(() => {
          setIsLiking(false);
        });
    }, 'generic');
  };

  const handleSave = (): void => {
    if (!item) {
      return;
    }
    requireAuth(() => {
      setIsSaving(true);
      setError(null);
      void api
        .saveCollectionItem({ kind: 'MEDIA', mediaItemId: item.id })
        .then(() => {
          setSuccess(t('common.library.saveVideoSaved'));
        })
        .catch((err: unknown) => {
          setError(err instanceof ApiError ? err.message : t('common.library.saveVideoError'));
        })
        .finally(() => {
          setIsSaving(false);
        });
    }, 'generic');
  };

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.videos.loading')}</p>;
  }

  if (!item || !data) {
    return (
      <section>
        <p className="fm-error">{error ?? t('common.videos.loadError')}</p>
        <Link to="/videos">{t('common.videos.backToCatalog')}</Link>
      </section>
    );
  }

  const { creator, related } = data;
  const creatorProfilePath = `/@${creator.username}`;

  return (
    <section className="video-detail">
      {item.mediaUrl ? (
        <video
          controls
          playsInline
          poster={item.thumbnailUrl ?? undefined}
          src={item.mediaUrl}
          className="video-detail__player"
          onTimeUpdate={(event) => {
            if (!user) return;
            const el = event.currentTarget;
            const positionSeconds = Math.floor(el.currentTime);
            if (positionSeconds <= 0 || positionSeconds % 5 !== 0) return;
            void api
              .updateMediaProgress(item.id, {
                positionSeconds,
                durationSeconds: Number.isFinite(el.duration)
                  ? Math.floor(el.duration)
                  : undefined,
              })
              .catch(() => undefined);
          }}
        />
      ) : (
        <div className="video-detail__locked">
          {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" /> : null}
          <p>{t('common.videos.lockedPreview')}</p>
        </div>
      )}

      <h1 className="video-detail__title">{item.title}</h1>

      <Link to={creatorProfilePath} className="video-detail__creator">
        {creator.avatarUrl ? (
          <img
            className="video-detail__creator-avatar"
            src={creator.avatarUrl}
            alt=""
            width={44}
            height={44}
          />
        ) : (
          <span className="video-detail__creator-avatar video-detail__creator-avatar--initials">
            {creatorInitials(creator.displayName)}
          </span>
        )}
        <span className="video-detail__creator-copy">
          <strong>{creator.displayName}</strong>
          <span>{formatUsername(creator.username)}</span>
        </span>
      </Link>

      <p className="video-detail__price">
        {item.owned
          ? t('common.videos.owned')
          : item.priceCoins === 0
            ? t('common.videos.free')
            : t('common.videos.coins').replace('{{amount}}', String(item.priceCoins))}
      </p>

      <div className="video-detail__actions">
        <Button
          label={
            item.liked
              ? t('common.videos.liked').replace('{{count}}', String(item.likeCount))
              : t('common.videos.like').replace('{{count}}', String(item.likeCount))
          }
          variant={item.liked ? 'primary' : 'ghost'}
          disabled={isLiking}
          onClick={handleLikeToggle}
        />
        <Button
          label={t('common.library.saveVideo')}
          variant="ghost"
          disabled={isSaving}
          onClick={handleSave}
        />
      </div>

      {item.description ? <p className="video-detail__description">{item.description}</p> : null}

      {wallet ? (
        <p style={{ color: 'var(--fm-color-text-muted)' }}>
          {t('common.videos.balance').replace('{{balance}}', String(wallet.total))}
        </p>
      ) : null}

      {success ? <p style={{ color: 'var(--fm-color-link)' }}>{success}</p> : null}
      {error ? <p className="fm-error">{error}</p> : null}

      {!item.owned && user && item.priceCoins > 0 ? (
        <div className="video-detail__actions">
          <Button
            label={
              isPurchasing
                ? t('common.videos.purchasing')
                : t('common.videos.purchase').replace('{{amount}}', String(item.priceCoins))
            }
            onClick={() => {
              void handlePurchase();
            }}
            disabled={isPurchasing}
          />
          <button
            type="button"
            className="show-room-modal__text-action"
            onClick={() => {
              openCoinShop(item.priceCoins);
            }}
          >
            {t('common.show.getMoreCoins')}
          </button>
        </div>
      ) : null}

      {!user ? (
        <p style={{ marginTop: '1rem' }}>
          <button
            type="button"
            className="show-room-modal__text-action"
            onClick={() => {
              openAuthModal({ mode: 'register', intent: 'purchase' });
            }}
          >
            {t('common.videos.signIn')}
          </button>
        </p>
      ) : null}

      {item.owned ? (
        <p style={{ marginTop: '1rem' }}>
          <Link to="/library">{t('common.nav.library')}</Link>
        </p>
      ) : null}

      <section className="video-detail__related" aria-labelledby="video-related-heading">
        <div className="video-detail__related-head">
          <h2 id="video-related-heading">
            {t('common.videos.moreFrom').replace('{{name}}', creator.displayName)}
          </h2>
          <Link to={creatorProfilePath} className="video-detail__see-all">
            {t('common.videos.viewCreator')}
          </Link>
        </div>
        <PagedRelatedGrid items={related} label={t('common.videos.moreFrom').replace('{{name}}', creator.displayName)} />
      </section>
    </section>
  );
}
